import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAppSystem } from '../hooks/useAppSystem';
import { useBusiness } from '../contexts/BusinessContext';
import BasicLoadingScreen from './BasicLoadingScreen';

interface AppRouteGuardProps {
  children: React.ReactNode;
  appSlug?: string; // The slug of the app required for this route
  requiresAppSystem?: boolean; // Whether this route requires the app system
}

export default function AppRouteGuard({ 
  children, 
  appSlug,
  requiresAppSystem = true 
}: AppRouteGuardProps) {
  const location = useLocation();
  const { isUsingAppSystem, coreApps, installedAddons, loading } = useAppSystem();
  const businessContext = useBusiness();
  const selectedBusiness = businessContext?.selectedBusiness;
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);

  useEffect(() => {
    // If still loading, don't make a decision yet - but only wait a reasonable amount of time
    if (loading) {
      setHasAccess(null);
      // Set a timeout to prevent infinite loading if useAppSystem gets stuck
      const timeout = setTimeout(() => {
        console.warn('AppRouteGuard: useAppSystem loading timeout, allowing access as fallback');
        setHasAccess(true); // Fallback to allowing access if loading takes too long
      }, 5000); // 5 second timeout
      return () => clearTimeout(timeout);
    }

    // If no business selected, no access
    if (!selectedBusiness) {
      setHasAccess(false);
      return;
    }

    // If not using app system, allow access (legacy mode)
    if (!isUsingAppSystem) {
      setHasAccess(true);
      return;
    }

    // If using app system but this route doesn't require it, allow
    if (!requiresAppSystem) {
      setHasAccess(true);
      return;
    }

    // If no specific app is required, allow access
    if (!appSlug) {
      setHasAccess(true);
      return;
    }

    // Check if the user has the required app installed
    const allApps = [...coreApps, ...installedAddons];
    const hasApp = allApps.some(app => app.app?.slug === appSlug && app.status === 'active');
    
    console.log('AppRouteGuard: Checking access for app:', appSlug, {
      hasApp,
      allAppsCount: allApps.length,
      coreAppsCount: coreApps.length,
      installedAddonsCount: installedAddons.length,
      appSlugs: allApps.map(a => a.app?.slug)
    });
    
    setHasAccess(hasApp);
  }, [isUsingAppSystem, coreApps, installedAddons, loading, selectedBusiness, appSlug, requiresAppSystem]);

  // While checking access, show loading
  if (hasAccess === null) {
    return <BasicLoadingScreen />;
  }

  // If no access, redirect to a default page
  if (!hasAccess) {
    // Redirect to calendar as it's likely a core app everyone has
    return <Navigate to="/calendarV2" replace />;
  }

  // Has access, render the children
  return <>{children}</>;
}

